import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import MarkdownEditor from '../components/MarkdownEditor';
import MarkdownRenderer from '../components/MarkdownRenderer';

interface WikiDocument {
  id: number;
  title: string;
  content: string | null;
  type: 'document' | 'folder';
  parent_id: number | null;
  permission: 'private' | 'public_viewable' | 'public_editable';
  created_by: number;
  updated_by?: number;
  created_at: string;
  updated_at: string;
  created_by_email?: string;
  updated_by_email?: string;
  is_favorite?: number; // 0 or 1 from database
}

interface WikiRevision {
  id: number;
  document_id: number;
  title: string;
  content: string | null;
  revision_number: number;
  created_by: number;
  created_at: string;
  created_by_email?: string;
}

interface WikiProps {
  companyId?: string; // Optional companyId prop for company page context
}

const Wiki: React.FC<WikiProps> = ({ companyId: propCompanyId }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // Use prop companyId if provided (from company page), otherwise use user's company_id
  const companyId = propCompanyId || user?.company_id?.toString();
  const isGlobalUser = !user?.company_id; // Agents are global users
  
  const [documents, setDocuments] = useState<WikiDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDocument, setSelectedDocument] = useState<WikiDocument | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingDocument, setEditingDocument] = useState<WikiDocument | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'document' as 'document' | 'folder',
    parent_id: '' as string | number,
    permission: 'private' as 'private' | 'public_viewable' | 'public_editable'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set());
  const [revisions, setRevisions] = useState<WikiRevision[]>([]);
  const [showRevisions, setShowRevisions] = useState(false);
  const [selectedRevision, setSelectedRevision] = useState<WikiRevision | null>(null);
  const [loadingRevisions, setLoadingRevisions] = useState(false);
  const [sidebarMinimized, setSidebarMinimized] = useState(false);

  useEffect(() => {
    if (companyId) {
      fetchDocuments();
    }
  }, [companyId]);

  // Check for document ID in URL and load it (skip when editor is open so Edit button works)
  useEffect(() => {
    if (showEditor) return;
    const queryParams = new URLSearchParams(location.search);
    const docId = queryParams.get('doc');
    if (docId && documents.length > 0) {
      const docIdNum = parseInt(docId);
      const doc = documents.find(d => d.id === docIdNum);
      // Only load if document exists, is a document (not folder), and not already selected
      if (doc && doc.type === 'document' && (!selectedDocument || selectedDocument.id !== docIdNum)) {
        // Load the document directly
        const loadDocument = async () => {
          try {
            const response = await api.get(`/wiki/${companyId}/documents/${doc.id}`);
            setSelectedDocument(response.data);
            setShowEditor(false);
            setEditingDocument(null);
            setShowRevisions(false);
            setSelectedRevision(null);
            await fetchRevisions(doc.id);
          } catch (error: any) {
            console.error('Error fetching document from URL:', error);
          }
        };
        loadDocument();
      }
    }
  }, [location.search, documents, companyId, selectedDocument, showEditor]);

  const fetchDocuments = async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      const response = await api.get(`/wiki/${companyId}/documents`);
      setDocuments(response.data);
    } catch (error: any) {
      console.error('Error fetching wiki documents:', error);
      alert(error.response?.data?.error || 'Failed to fetch wiki documents');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = (type: 'document' | 'folder' = 'document', parentId: number | null = null) => {
    setEditingDocument(null);
    setFormData({
      title: '',
      content: '',
      type,
      parent_id: parentId === null ? '' : parentId,
      permission: isGlobalUser ? 'private' : 'public_editable' // Customers can only create public_editable documents
    });
    setShowEditor(true);
    setSelectedDocument(null);
  };

  const handleEdit = (doc: WikiDocument) => {
    setEditingDocument(doc);
    setFormData({
      title: doc.title,
      content: doc.content || '',
      type: doc.type,
      parent_id: doc.parent_id === null ? '' : doc.parent_id,
      permission: doc.permission || 'private'
    });
    setShowEditor(true);
    setSelectedDocument(null);
  };

  const handleView = async (doc: WikiDocument) => {
    if (doc.type === 'folder') {
      // Toggle folder expansion
      const newExpanded = new Set(expandedFolders);
      if (newExpanded.has(doc.id)) {
        newExpanded.delete(doc.id);
      } else {
        newExpanded.add(doc.id);
      }
      setExpandedFolders(newExpanded);
      return;
    }

    try {
      const response = await api.get(`/wiki/${companyId}/documents/${doc.id}`);
      setSelectedDocument(response.data);
      setShowEditor(false);
      setEditingDocument(null);
      setShowRevisions(false);
      setSelectedRevision(null);
      
      // Update URL with document ID
      const queryParams = new URLSearchParams(location.search);
      queryParams.set('doc', doc.id.toString());
      navigate(`${location.pathname}?${queryParams.toString()}`, { replace: true });
      
      // Load revisions if it's a document (not a folder)
      await fetchRevisions(doc.id);
    } catch (error: any) {
      console.error('Error fetching document:', error);
      alert(error.response?.data?.error || 'Failed to fetch document');
    }
  };

  const handleCopyLink = async () => {
    if (!selectedDocument) return;
    
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('tab', 'wiki');
    queryParams.set('doc', selectedDocument.id.toString());
    const link = `${window.location.origin}${location.pathname}?${queryParams.toString()}`;
    
    try {
      await navigator.clipboard.writeText(link);
      // Show temporary success message
      const button = document.getElementById('copy-link-button');
      if (button) {
        const originalText = button.textContent;
        button.textContent = 'Copied!';
        button.classList.add('bg-green-600', 'hover:bg-green-700');
        button.classList.remove('bg-indigo-600', 'hover:bg-indigo-700');
        setTimeout(() => {
          button.textContent = originalText;
          button.classList.remove('bg-green-600', 'hover:bg-green-700');
          button.classList.add('bg-indigo-600', 'hover:bg-indigo-700');
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to copy link:', error);
      alert('Failed to copy link to clipboard');
    }
  };

  const fetchRevisions = async (documentId: number) => {
    setLoadingRevisions(true);
    try {
      const response = await api.get(`/wiki/${companyId}/documents/${documentId}/revisions`);
      setRevisions(response.data);
    } catch (error: any) {
      console.error('Error fetching revisions:', error);
      setRevisions([]);
    } finally {
      setLoadingRevisions(false);
    }
  };

  const handleViewRevision = async (revisionId: number) => {
    if (!selectedDocument) return;
    
    try {
      const response = await api.get(`/wiki/${companyId}/documents/${selectedDocument.id}/revisions/${revisionId}`);
      setSelectedRevision(response.data);
    } catch (error: any) {
      console.error('Error fetching revision:', error);
      alert('Failed to load revision');
    }
  };

  const handleRestoreRevision = async (revisionId: number) => {
    if (!selectedDocument) return;
    
    if (!window.confirm('Are you sure you want to restore this revision? This will create a new revision with the current content.')) {
      return;
    }

    try {
      await api.post(`/wiki/${companyId}/documents/${selectedDocument.id}/revisions/${revisionId}/restore`);
      alert('Revision restored successfully');
      // Refresh the document list first
      await fetchDocuments();
      // Then refresh the selected document and its revisions
      const response = await api.get(`/wiki/${companyId}/documents/${selectedDocument.id}`);
      setSelectedDocument(response.data);
      await fetchRevisions(selectedDocument.id);
      setSelectedRevision(null);
    } catch (error: any) {
      console.error('Error restoring revision:', error);
      alert(error.response?.data?.error || 'Failed to restore revision');
    }
  };

  const handleToggleFavorite = async (doc: WikiDocument, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    
    try {
      const response = await api.post(`/wiki/${companyId}/documents/${doc.id}/favorite`);
      // Update the document in the list
      setDocuments(docs => docs.map(d => 
        d.id === doc.id ? { ...d, is_favorite: response.data.favorited ? 1 : 0 } : d
      ));
      // Update selected document if it's the one being favorited
      if (selectedDocument && selectedDocument.id === doc.id) {
        setSelectedDocument({ ...selectedDocument, is_favorite: response.data.favorited ? 1 : 0 });
      }
    } catch (error: any) {
      console.error('Error toggling favorite:', error);
      alert(error.response?.data?.error || 'Failed to update favorite');
    }
  };

  const handleDelete = async (doc: WikiDocument) => {
    const itemType = doc.type === 'folder' ? 'folder' : 'document';
    const message = doc.type === 'folder'
      ? `Are you sure you want to delete the folder "${doc.title}"? All nested items will also be deleted.`
      : `Are you sure you want to delete "${doc.title}"?`;
    
    if (!window.confirm(message)) {
      return;
    }

    try {
      await api.delete(`/wiki/${companyId}/documents/${doc.id}`);
      fetchDocuments();
      if (selectedDocument?.id === doc.id) {
        setSelectedDocument(null);
      }
    } catch (error: any) {
      console.error('Error deleting document:', error);
      alert(error.response?.data?.error || 'Failed to delete document');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        parent_id: formData.parent_id === '' ? null : Number(formData.parent_id),
        content: formData.type === 'folder' ? null : formData.content,
        // Customers can only create/edit with public_editable permission
        permission: isGlobalUser ? formData.permission : 'public_editable'
      };

      if (editingDocument) {
        await api.put(`/wiki/${companyId}/documents/${editingDocument.id}`, data);
      } else {
        await api.post(`/wiki/${companyId}/documents`, data);
      }
      setShowEditor(false);
      setEditingDocument(null);
      fetchDocuments();
    } catch (error: any) {
      console.error('Error saving document:', error);
      alert(error.response?.data?.error || error.response?.data?.errors?.map((e: any) => e.msg).join(', ') || 'Failed to save document');
    }
  };

  const filteredDocuments = useMemo(() => {
    if (!searchTerm) return documents;
    const term = searchTerm.toLowerCase();
    return documents.filter(doc =>
      doc.title.toLowerCase().includes(term) ||
      (doc.content && doc.content.toLowerCase().includes(term))
    );
  }, [documents, searchTerm]);

  // Build tree structure
  const renderDocumentTree = (parentId: number | null = null, depth = 0, favoritesOnly = false) => {
    let items = filteredDocuments.filter(d => d.parent_id === parentId);
    
    // If favoritesOnly is true, only show documents that are favorited
    if (favoritesOnly) {
      items = items.filter(d => d.is_favorite === 1 && d.type === 'document');
    }
    
    if (items.length === 0 && parentId !== null) return null;

    return (
      <ul className={`${depth > 0 ? 'ml-6 border-l border-gray-200 dark:border-gray-700 pl-4' : ''}`}>
        {items.map(item => {
          const isExpanded = expandedFolders.has(item.id);
          const hasChildren = filteredDocuments.some(d => d.parent_id === item.id);
          
          return (
            <li key={item.id} className="py-2">
              <div className="flex items-center justify-between group">
                <div 
                  className="flex items-center space-x-2 flex-1 cursor-pointer"
                  onClick={() => handleView(item)}
                >
                  {item.type === 'folder' ? (
                    <>
                      <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                      </svg>
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{item.title}</span>
                      {hasChildren && (
                        <svg 
                          className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      )}
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.title}</span>
                      <button
                        onClick={(e) => handleToggleFavorite(item, e)}
                        className={`ml-2 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                          item.is_favorite === 1 ? 'text-yellow-500' : 'text-gray-400'
                        }`}
                        title={item.is_favorite === 1 ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <svg className="w-4 h-4" fill={item.is_favorite === 1 ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                      </button>
                    </>
                  )}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                    item.permission === 'public_editable' 
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      : item.permission === 'public_viewable'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                  }`}>
                    {item.permission === 'public_editable' 
                      ? 'Shared (Read/Write)'
                      : item.permission === 'public_viewable'
                      ? 'Shared (Read-only)'
                      : 'Private'}
                  </span>
                </div>
                
                {(isGlobalUser || item.permission === 'public_editable') && (
                  <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                    {item.type === 'folder' && (isGlobalUser || item.permission === 'public_editable') && (
                      <>
                        <button
                          onClick={() => handleCreateNew('document', item.id)}
                          className="p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                          title="Add Document to this folder"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </button>
                        {isGlobalUser && (
                          <button
                            onClick={() => handleCreateNew('folder', item.id)}
                            className="p-1 text-gray-400 hover:text-yellow-600 dark:hover:text-yellow-400"
                            title="Add Subfolder"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                            </svg>
                          </button>
                        )}
                      </>
                    )}
                    <button
                      onClick={() => handleEdit(item)}
                      className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                      title="Edit"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    {isGlobalUser && (
                      <button
                        onClick={() => handleDelete(item)}
                        className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </div>
              {item.type === 'folder' && isExpanded && renderDocumentTree(item.id, depth + 1, favoritesOnly)}
            </li>
          );
        })}
      </ul>
    );
  };

  // Get all folders for parent selection (excluding current item if editing)
  const availableFolders = documents.filter(d => d.type === 'folder' && d.id !== editingDocument?.id);

  // For global users, we need a company ID - redirect or show message
  if (!companyId && !isGlobalUser) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">You must be associated with a company to view the wiki.</p>
        </div>
      </div>
    );
  }
  
  // For global users viewing from company page, companyId should be provided via prop
  // If not provided and no companyId, show a message
  if (!companyId) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">Please select a company to view its wiki.</p>
        </div>
      </div>
    );
  }

  // Collapse/expand all folders
  const handleToggleAllFolders = () => {
    const allFolderIds = documents.filter(d => d.type === 'folder').map(d => d.id);
    if (expandedFolders.size === allFolderIds.length) {
      // All are expanded, collapse all
      setExpandedFolders(new Set());
    } else {
      // Expand all
      setExpandedFolders(new Set(allFolderIds));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-1 ${showEditor ? 'lg:grid-cols-1' : sidebarMinimized ? 'lg:grid-cols-12' : 'lg:grid-cols-3'} gap-6`}>
      {/* Sidebar - Document Tree */}
      {!showEditor && (
        <div className={sidebarMinimized ? 'lg:col-span-1' : 'lg:col-span-1'}>
        <div className={`bg-white dark:bg-gray-800 shadow rounded-lg p-4 transition-all duration-300 ${sidebarMinimized ? 'w-16' : ''}`}>
          {!sidebarMinimized ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Wiki</h2>
                {(isGlobalUser || documents.some(d => d.permission === 'public_editable')) && (
                  <div className="flex gap-1">
                    {isGlobalUser && (
                      <button
                        onClick={() => handleCreateNew('folder')}
                        className="inline-flex items-center px-2 py-1 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                        title="New Folder"
                      >
                        <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                        </svg>
                      </button>
                    )}
                    <button
                      onClick={() => handleCreateNew('document')}
                      className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
                      title="New Document"
                    >
                      + New
                    </button>
                  </div>
                )}
              </div>

              {/* Sidebar Controls */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={handleToggleAllFolders}
                  className="inline-flex items-center px-2 py-1 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                  title={expandedFolders.size > 0 ? "Collapse All Folders" : "Expand All Folders"}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {expandedFolders.size > 0 ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    )}
                  </svg>
                </button>
                <button
                  onClick={() => setSidebarMinimized(!sidebarMinimized)}
                  className="inline-flex items-center px-2 py-1 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                  title="Minimize Sidebar"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center gap-4">
              <h2 className="text-xs font-semibold text-gray-900 dark:text-gray-100 transform -rotate-90 whitespace-nowrap">Wiki</h2>
              <button
                onClick={() => setSidebarMinimized(!sidebarMinimized)}
                className="inline-flex items-center px-2 py-1 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                title="Expand Sidebar"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}

          {!sidebarMinimized && (
            <>

              {/* Search */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              {/* Document Tree */}
              <div className="space-y-1 max-h-[600px] overflow-y-auto">
                {filteredDocuments.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                    {searchTerm ? 'No documents found' : 'No documents yet'}
                  </p>
                ) : (
                  <>
                    {/* Favorites Folder */}
                    {filteredDocuments.some(d => d.is_favorite === 1 && d.type === 'document') && (
                      <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center space-x-2 mb-2">
                          <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Favorites</span>
                        </div>
                        {renderDocumentTree(null, 0, true)}
                      </div>
                    )}
                    {/* Regular Document Tree */}
                    {renderDocumentTree(null)}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      )}

      {/* Main Content - Editor or Viewer */}
      <div className={showEditor ? 'lg:col-span-1' : sidebarMinimized ? 'lg:col-span-11' : 'lg:col-span-2'}>
        {showEditor ? (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {editingDocument ? 'Edit' : 'Create'} {formData.type === 'folder' ? 'Folder' : 'Document'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  required
                />
              </div>

              {formData.type === 'document' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Content
                  </label>
                  <MarkdownEditor
                    value={formData.content}
                    onChange={(value) => setFormData({ ...formData, content: value })}
                    placeholder="Write your wiki content here..."
                  />
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Parent Folder
                </label>
                <select
                  value={formData.parent_id}
                  onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">Root</option>
                  {availableFolders.map(folder => (
                    <option key={folder.id} value={folder.id}>{folder.title}</option>
                  ))}
                </select>
              </div>

              {isGlobalUser && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Permission Level
                  </label>
                  <select
                    value={formData.permission}
                    onChange={(e) => setFormData({ ...formData, permission: e.target.value as 'private' | 'public_viewable' | 'public_editable' })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="private">Private (Only agents can view/edit)</option>
                    <option value="public_viewable">Shared (Read-only) (Customers can view, agents can edit)</option>
                    <option value="public_editable">Shared (Read/Write) (Customers can view and edit)</option>
                  </select>
                </div>
              )}
              {!isGlobalUser && !editingDocument && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Permission Level
                  </label>
                  <input
                    type="text"
                    value="Shared (Read/Write) (Customers can view and edit)"
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm sm:text-sm bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Documents you create will be shared with read/write access
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
                >
                  {editingDocument ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditor(false);
                    setEditingDocument(null);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        ) : selectedDocument ? (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {selectedDocument.title}
                </h2>
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
                  <span>
                    Created: {new Date(selectedDocument.created_at).toLocaleString()}
                    {selectedDocument.created_by_email && ` by ${selectedDocument.created_by_email}`}
                  </span>
                  {selectedDocument.updated_at !== selectedDocument.created_at && (
                    <span>
                      Updated: {new Date(selectedDocument.updated_at).toLocaleString()}
                      {selectedDocument.updated_by_email && ` by ${selectedDocument.updated_by_email}`}
                    </span>
                  )}
                </div>
                <div className="mt-2">
                  {selectedDocument.permission === 'public_editable' ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                      Shared (Read/Write)
                    </span>
                  ) : selectedDocument.permission === 'public_viewable' ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                      Shared (Read-only)
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                      Private
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={(e) => handleToggleFavorite(selectedDocument, e)}
                  className={`inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md ${
                    selectedDocument.is_favorite === 1
                      ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border-yellow-300 dark:border-yellow-700'
                      : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                  title={selectedDocument.is_favorite === 1 ? 'Remove from favorites' : 'Add to favorites'}
                >
                  <svg className="w-4 h-4 mr-1.5" fill={selectedDocument.is_favorite === 1 ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                  {selectedDocument.is_favorite === 1 ? 'Favorited' : 'Favorite'}
                </button>
                <button
                  onClick={handleCopyLink}
                  id="copy-link-button"
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
                  title="Copy link to this document"
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy Link
                </button>
                {(isGlobalUser || selectedDocument.permission === 'public_editable') && (
                  <button
                    onClick={() => handleEdit(selectedDocument)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
                  >
                    Edit
                  </button>
                )}
                {selectedDocument.type === 'document' && (
                  <button
                    onClick={() => setShowRevisions(!showRevisions)}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    {showRevisions ? 'Hide' : 'Show'} Revisions ({revisions.length})
                  </button>
                )}
              </div>
            </div>
            
            {showRevisions && selectedDocument.type === 'document' && (
              <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Revision History</h3>
                {loadingRevisions ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                  </div>
                ) : revisions.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No revisions available</p>
                ) : (
                  <div className="space-y-2">
                    {revisions.map((revision) => (
                      <div
                        key={revision.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedRevision?.id === revision.id
                            ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                        onClick={() => handleViewRevision(revision.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                Revision {revision.revision_number}
                              </span>
                              {revision.created_by_email && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  by {revision.created_by_email}
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(revision.created_at).toLocaleString()}
                            </span>
                          </div>
                          {(isGlobalUser || selectedDocument.permission === 'public_editable') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRestoreRevision(revision.id);
                              }}
                              className="px-2 py-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                              title="Restore this revision"
                            >
                              Restore
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {selectedRevision && (
              <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Revision {selectedRevision.revision_number} Preview
                  </h3>
                  <button
                    onClick={() => setSelectedRevision(null)}
                    className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    Close Preview
                  </button>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-3">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                    {selectedRevision.title}
                  </h4>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(selectedRevision.created_at).toLocaleString()}
                    {selectedRevision.created_by_email && ` by ${selectedRevision.created_by_email}`}
                  </div>
                </div>
                {selectedRevision.content && (
                  <div className="prose dark:prose-invert max-w-none">
                    <MarkdownRenderer content={selectedRevision.content} />
                  </div>
                )}
              </div>
            )}
            
            {!selectedRevision && selectedDocument.content && (
              <div className="prose dark:prose-invert max-w-none">
                <MarkdownRenderer content={selectedDocument.content} />
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-12 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No document selected</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Select a document from the list to view it, or create a new one.
            </p>
            {(isGlobalUser || documents.some(d => d.permission === 'public_editable')) && (
              <div className="mt-6">
                <button
                  onClick={() => handleCreateNew('document')}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
                >
                  Create New Document
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Wiki;
